import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Container, Stack } from '@mui/material';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { useTimeoutFn, useUpdateEffect } from 'react-use';

import { authClient, UserModel } from '@merchant-clients/auth';

import { AppRoutes } from '@frontend/app-routes';
import { RiveAnimation, RiveAnimationRef } from '@frontend/components';
import { LocaleProvider, useClientQuery } from '@frontend/hooks';
import { useI18n } from '@frontend/locales';

import { LoadingProvider } from './loading-context';
import { PersistentUiSettingsProvider } from './persistent-ui-settings-context';
import { Prefetch } from './prefetch';
import { ShopProvider } from './shop-context';
import { useRoutesImporter } from './use-routes-importer';
import { UserProvider } from './user-context';

export interface InitializeProps {
  sessionValidationKey: number;
  isValidSession: boolean;
  onValidSessionChanged(isValid: boolean): void;
}

export const Initialize = ({
  sessionValidationKey,
  isValidSession,
  onValidSessionChanged,
}: PropsWithChildren<InitializeProps>) => {
  const { loading: i18nLoading, error, language, locale } = useI18n();
  const { routes, loading: routesLoading } = useRoutesImporter();
  const riveRef = useRef<RiveAnimationRef>(null);
  const handleTrigger = useCallback(
    (inputName: string, value?: number | boolean) =>
      riveRef.current?.trigger(inputName, value),
    [],
  );
  const [isLoadingAnimationComplete, setIsLoadingAnimationComplete] =
    useState(false); // rive emits event "complete" when loading-animation is finished.
  const [user, setUser] = useState<UserModel | undefined>(undefined);

  const usesSplashScreen = Capacitor.getPlatform() === 'ios';
  const queryClient = useQueryClient();

  const activeRouter = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '*',
          element: <AppRoutes validSession={isValidSession} routes={routes!} />,
        },
      ]),
    [isValidSession, routes],
  );

  useUpdateEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['verify-session'] });
    setIsLoadingAnimationComplete(false);
  }, [sessionValidationKey]);

  const { isPending: loadingVerification } = useClientQuery({
    queryKey: ['verify-session'],
    onFetch: async () => {
      return await authClient
        .getActiveUser()
        .then((activeUser) => {
          setIsLoadingAnimationComplete(true);
          onValidSessionChanged(true);
          setUser(activeUser);
          return true;
        })
        .catch(() => {
          onValidSessionChanged(false);
          return false;
        });
    },
  });

  useTimeoutFn(() => {
    if (usesSplashScreen) {
      SplashScreen.hide().catch((error) => {
        console.error(error);
        captureException(error);
      });
    }
  }, APP_CONFIG.app.splashDelay);

  useEffect(() => {
    if (!usesSplashScreen) {
      SplashScreen.hide().catch((error) => {
        console.error(error);
        captureException(error);
      });
    }
  }, [usesSplashScreen]);

  const isSessionDataLoading = isValidSession ? !user : false;

  const isLoading =
    i18nLoading || routesLoading || loadingVerification || isSessionDataLoading;

  useEffect(() => {
    if (!isLoading) {
      handleTrigger('success', true);
    }
  }, [isLoading, handleTrigger]);

  if (error) {
    return <>{`Failed to load locales: ${error}`}</>;
  }

  return (
    <>
      {(!isLoadingAnimationComplete || !routes) && (
        <Container component="main" maxWidth="xs">
          <Stack
            spacing={2}
            height="100vh"
            justifyContent="center"
            alignItems="center"
          >
            <RiveAnimation
              ref={riveRef}
              src="/rive/loading.riv"
              height={128}
              onEvent={() => {
                SplashScreen.hide().catch((error) => {
                  console.error(error);
                  captureException(error);
                });
                setIsLoadingAnimationComplete(true);
              }}
            />
          </Stack>
        </Container>
      )}
      {isLoadingAnimationComplete && isValidSession && user && routes && (
        <UserProvider user={user} setUser={setUser}>
          <PersistentUiSettingsProvider>
            <Prefetch>
              <ShopProvider>
                <LocaleProvider language={language} locale={locale}>
                  <LoadingProvider>
                    <RouterProvider router={activeRouter} />
                  </LoadingProvider>
                </LocaleProvider>
              </ShopProvider>
            </Prefetch>
          </PersistentUiSettingsProvider>
        </UserProvider>
      )}

      {isLoadingAnimationComplete && !isValidSession && routes && (
        <UserProvider user={undefined} setUser={setUser}>
          <RouterProvider router={activeRouter} />
        </UserProvider>
      )}
    </>
  );
};
